import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Landing from "@/pages/Landing";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import WorkspaceLogin from "@/pages/WorkspaceLogin";
import Workspace from "@/pages/Workspace";
import ProCRM from "@/pages/ProCRM";
import ProCalendar from "@/pages/ProCalendar";
import AuthCallback from "@/pages/AuthCallback";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { HomeownerSignup, HomeownerLogin } from "@/pages/HomeownerAuth";
import { ProSignup, ProLogin } from "@/pages/ProAuth";
import ProProfileEdit from "@/pages/ProProfileEdit";
import HomeownerDashboard from "@/pages/HomeownerDashboard";
import HomeownerProfile from "@/pages/HomeownerProfile";
import ProPublicProfile from "@/pages/ProPublicProfile";
import { AuthProvider } from "@/context/AuthContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { HomeownerAuthProvider } from "@/context/HomeownerAuthContext";
import { Toaster } from "@/components/ui/sonner";

// CRITICAL: detect the Google OAuth callback session_id in the URL fragment
// synchronously during render (NOT inside useEffect). If present we mount
// AuthCallback which exchanges it for a session cookie before anything else.
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/workspace/login" element={<WorkspaceLogin />} />
      <Route path="/workspace" element={<Workspace />} />
      <Route path="/workspace/calendar" element={<ProCalendar />} />
      <Route path="/workspace/jobs/:jobId" element={<ProCRM />} />
      <Route path="/workspace/profile" element={<ProProfileEdit />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/homeowner/signup" element={<HomeownerSignup />} />
      <Route path="/homeowner/login" element={<HomeownerLogin />} />
      <Route path="/homeowner/dashboard" element={<HomeownerDashboard />} />
      <Route path="/homeowner/profile" element={<HomeownerProfile />} />
      {/* Pro auth — MUST come before /pro/:id */}
      <Route path="/pro/signup" element={<ProSignup />} />
      <Route path="/pro/login" element={<ProLogin />} />
      <Route path="/pro/:id" element={<ProPublicProfile />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Suppress the harmless ResizeObserver notification loop that triggers
    // the CRA dev-error overlay. Framer-motion + lenis can emit this in dev.
    const RO_MSG = "ResizeObserver loop";
    const onErr = (e) => {
      if (e?.message && e.message.includes(RO_MSG)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    const onRej = (e) => {
      if (typeof e?.reason === "string" && e.reason.includes(RO_MSG)) {
        e.preventDefault();
      }
    };
    window.addEventListener("error", onErr, true);
    window.addEventListener("unhandledrejection", onRej, true);
    return () => {
      window.removeEventListener("error", onErr, true);
      window.removeEventListener("unhandledrejection", onRej, true);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <HomeownerAuthProvider>
            <AdminAuthProvider>
              <AppRouter />
            </AdminAuthProvider>
          </HomeownerAuthProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
